import { FetchResult } from '@apollo/client';
import { InitOptions } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';

import { CreateThesisImageMutation } from 'interfaces/graphql-typings';

export const tinymceConfig: InitOptions = {
  branding: false,
  content_style: 'body { font-family: Merriweather; }',
  elementpath: false,
  inline: false,
  relative_urls: false,
  invalid_elements:
    'font,embed,param,script,html,body,head,meta,title,link,iframe,applet,noframes,noscript,form,input,select,option,std,xml:,st1:,o:,w:,v:',
  height: 350,
  plugins: 'autolink link lists table',
  toolbar: 'undo redo | bold italic | bullist numlist | table | link hr',
  menubar: false,
  min_height: 150,
  extended_valid_elements: 'a[!href|target=_blank|class=lightLink]'
};

// image uploading integration, see: https://www.tiny.cloud/docs/tinymce/6/image/#images_upload_handler
// with uploadConfig, images are uploaded immediately to the server and then inserted into the editor (<img src="/pro/research/thesis_images/<thesis_image.id>">)
// without uploadConfig, images are inserted directly into the editor as base64 strings (<img src="data:...;base64,...">)
// if the image has an invalid type, or if there is an error uploading the image, the image will be removed from the editor
export const withThesisImageConfig = (uploadConfig?: {
  imageableId: string;
  createThesisImage: (input: {
    imageableId: string;
    image: string;
  }) => Promise<FetchResult<CreateThesisImageMutation>>;
}): InitOptions => ({
  branding: false,
  content_style: 'body { font-family: Merriweather; }',
  elementpath: false,
  inline: false,
  relative_urls: true,
  invalid_elements:
    'font,embed,param,script,html,body,head,meta,title,link,iframe,applet,noframes,noscript,form,input,select,option,std,xml:,st1:,o:,w:,v:',
  height: 350,
  plugins: 'autolink link image lists table quickbars',
  toolbar: 'undo redo | bold italic | bullist numlist | table quickimage | link hr',
  menubar: false,
  min_height: 150,
  convert_urls: false,
  valid_children: '+body[style],+p[strong|a|#text|i]',
  extended_valid_elements: 'a[!href|target=_blank|class=lightLink|name=[*]],style,i',
  quickbars_insert_toolbar: false,
  quickbars_selection_toolbar: 'bold italic | bullist numlist | link',
  images_file_types: 'jpg,jpeg,gif,png',
  images_upload_handler: blobInfo =>
    new Promise(async (resolve, reject) => {
      const allowedFileTypes = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];

      const fileType = blobInfo.blob().type;

      if (!allowedFileTypes.includes(fileType))
        return reject({
          message: 'Please upload a jpg, jpeg, gif, or png file.',
          remove: true
        });

      const image = `data:${fileType};base64,${blobInfo.base64()}`;

      if (!uploadConfig) return resolve(image);

      const { imageableId, createThesisImage } = uploadConfig;

      return createThesisImage({
        imageableId,
        image
      })
        .then(({ data }) => {
          const thesisImageUrl = data?.createThesisImage?.thesisImageUrl;

          if (!thesisImageUrl)
            return reject({
              message:
                'Please try again. If the problem persists, please contact support@sumzero.com for assistance.',
              remove: true
            });

          return resolve(thesisImageUrl);
        })
        .catch(error =>
          reject({
            message: error.message,
            remove: true
          })
        );
    })
});

export default tinymceConfig;
